import { Component, computed, inject, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Subscription, merge, Observable, of } from 'rxjs';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { LutService } from '../../../../../services/lut.service';
import { CompanyEmployeeMetricsService } from '../../../../../services/companies/company-employee-metrics.service';
import { CompanyEmployeeMetricsDto, UpsertEmployeeMetricsRequest } from '../../../../../models/api/companyEmployeeMetricsDto';
import { CompanyBoardService } from '../../../../../services/companies/company-board.service';
import { BoardMemberDto } from '../../../../../models/api/companyBoardMemberDto';
import { Currency } from '../../../../../models/currency';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-employees-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    ReadOnlyTabDirective,
  ],
  templateUrl: './employees-tab.component.html',
  styleUrl: './employees-tab.component.scss'
})
export class EmployeesTabComponent implements OnInit, OnDestroy, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private lutService = inject(LutService);
  private employeeMetricsService = inject(CompanyEmployeeMetricsService);
  private boardService = inject(CompanyBoardService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Employees));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  isCarriedForward = false;
  showKeywords = true;
  otherMeasurement: string = '';
  otherData: CompanyEmployeeMetricsDto | null = null;

  currencies: Currency[] = [];

  totalTypes = [
    { value: 0, label: 'Total' },
    { value: 1, label: 'Permanent' },
    { value: 2, label: 'Full' }
  ];

  private calcSubscriptions = new Subscription();

  private permTempLastEdited: 'permanent' | 'temporary' | 'permanentPct' | 'temporaryPct' | null = null;
  private fullPartLastEdited: 'fullTime' | 'partTime' | 'fullTimePct' | 'partTimePct' | null = null;
  private hiresLastEdited: 'newEmployeeHires' | 'newEmployeeHiresPct' | null = null;
  private turnoverLastEdited: 'employeeTurnover' | 'employeeTurnoverPct' | null = null;
  private voluntaryLastEdited: 'voluntaryTurnover' | 'voluntaryTurnoverPct' | null = null;
  private disabledLastEdited: 'num' | 'pct' | null = null;
  private hsTotalManual = { accidents: false, fatalities: false, lostWorkingDays: false };
  private newHiresDemoLastEdited: Record<string, 'num' | 'pct' | null> = {};
  private turnoverDemoLastEdited: Record<string, 'num' | 'pct' | null> = {};
  private leadershipDemoLastEdited: Record<string, 'num' | 'pct' | null> = {};
  private employeesDemoLastEdited: Record<string, 'num' | 'pct' | null> = {};
  /** Which gender the user drove last in each sub-table — the other one is what gives way. */
  private genderLastEdited: Record<string, 'Male' | 'Female' | null> = {};
  private boardMemberCount: number | null = null;

  form: FormGroup = this.fb.group({
    // Hires & Turnovers top table
    total: [null as number | null],
    totalPct: [null as number | null],
    totalType: [0 as number | null],
    permanent: [null as number | null],
    permanentPct: [null as number | null],
    temporary: [null as number | null],
    temporaryPct: [null as number | null],
    fullTime: [null as number | null],
    fullTimePct: [null as number | null],
    partTime: [null as number | null],
    partTimePct: [null as number | null],
    contractors: [null as number | null],
    newEmployeeHires: [null as number | null],
    newEmployeeHiresPct: [null as number | null],
    employeeTurnover: [null as number | null],
    employeeTurnoverPct: [null as number | null],
    voluntaryTurnover: [null as number | null],
    voluntaryTurnoverPct: [null as number | null],

    // Health & Safety top table
    injuryRate: [null as number | null],
    accidents: [null as number | null],
    fatalities: [null as number | null],
    lostTimeIncidentRate: [null as number | null],
    totalRecordableIncidentRate: [null as number | null],
    lostWorkingDays: [null as number | null],
    absenteeRate: [null as number | null],
    contractorsInjuryRate: [null as number | null],
    contractorsAccidents: [null as number | null],
    contractorsFatalities: [null as number | null],
    contractorsLostTimeIncidentRate: [null as number | null],
    contractorsTotalRecordableIncidentRate: [null as number | null],
    contractorsLostWorkingDays: [null as number | null],
    contractorsAbsenteeRate: [null as number | null],
    totalAccidents: [null as number | null],
    totalFatalities: [null as number | null],
    totalLostWorkingDays: [null as number | null],

    // Demographics - New Hires
    newHiresMale: [null as number | null],
    newHiresMalePct: [null as number | null],
    newHiresFemale: [null as number | null],
    newHiresFemalePct: [null as number | null],
    newHiresLgbt: [null as number | null],
    newHiresLgbtPct: [null as number | null],
    newHiresUnderThirty: [null as number | null],
    newHiresUnderThirtyPct: [null as number | null],
    newHiresOverThirtyUnderFifty: [null as number | null],
    newHiresOverThirtyUnderFiftyPct: [null as number | null],
    newHiresOverFifty: [null as number | null],
    newHiresOverFiftyPct: [null as number | null],
    newHiresNonClassified: [null as number | null],
    newHiresNonClassifiedPct: [null as number | null],
    newHiresAmerica: [null as number | null],
    newHiresAmericaPct: [null as number | null],
    newHiresAsiaPacific: [null as number | null],
    newHiresAsiaPacificPct: [null as number | null],
    newHiresEuropeAfrica: [null as number | null],
    newHiresEuropeAfricaPct: [null as number | null],
    newHiresOtherRegion: [null as number | null],
    newHiresOtherRegionPct: [null as number | null],
    newHiresWhites: [null as number | null],
    newHiresWhitesPct: [null as number | null],
    newHiresBlacks: [null as number | null],
    newHiresBlacksPct: [null as number | null],
    newHiresAsian: [null as number | null],
    newHiresAsianPct: [null as number | null],
    newHiresHispanic: [null as number | null],
    newHiresHispanicPct: [null as number | null],
    newHiresOtherMinority: [null as number | null],
    newHiresOtherMinorityPct: [null as number | null],

    // Demographics - Turnover
    turnoverMale: [null as number | null],
    turnoverMalePct: [null as number | null],
    turnoverFemale: [null as number | null],
    turnoverFemalePct: [null as number | null],
    turnoverLgbt: [null as number | null],
    turnoverLgbtPct: [null as number | null],
    turnoverUnderThirty: [null as number | null],
    turnoverUnderThirtyPct: [null as number | null],
    turnoverOverThirtyUnderFifty: [null as number | null],
    turnoverOverThirtyUnderFiftyPct: [null as number | null],
    turnoverOverFifty: [null as number | null],
    turnoverOverFiftyPct: [null as number | null],
    turnoverNonClassified: [null as number | null],
    turnoverNonClassifiedPct: [null as number | null],
    turnoverAmerica: [null as number | null],
    turnoverAmericaPct: [null as number | null],
    turnoverAsiaPacific: [null as number | null],
    turnoverAsiaPacificPct: [null as number | null],
    turnoverEuropeAfrica: [null as number | null],
    turnoverEuropeAfricaPct: [null as number | null],
    turnoverOtherRegion: [null as number | null],
    turnoverOtherRegionPct: [null as number | null],
    turnoverWhites: [null as number | null],
    turnoverWhitesPct: [null as number | null],
    turnoverBlacks: [null as number | null],
    turnoverBlacksPct: [null as number | null],
    turnoverAsian: [null as number | null],
    turnoverAsianPct: [null as number | null],
    turnoverHispanic: [null as number | null],
    turnoverHispanicPct: [null as number | null],
    turnoverOtherMinority: [null as number | null],
    turnoverOtherMinorityPct: [null as number | null],

    // Demographics - Board
    boardMale: [null as number | null],
    boardMalePct: [null as number | null],
    boardFemale: [null as number | null],
    boardFemalePct: [null as number | null],
    boardLgbt: [null as number | null],
    boardLgbtPct: [null as number | null],
    boardUnderThirty: [null as number | null],
    boardUnderThirtyPct: [null as number | null],
    boardOverThirtyUnderFifty: [null as number | null],
    boardOverThirtyUnderFiftyPct: [null as number | null],
    boardOverFifty: [null as number | null],
    boardOverFiftyPct: [null as number | null],
    boardNonClassified: [null as number | null],
    boardNonClassifiedPct: [null as number | null],
    boardAmerica: [null as number | null],
    boardAmericaPct: [null as number | null],
    boardAsiaPacific: [null as number | null],
    boardAsiaPacificPct: [null as number | null],
    boardEuropeAfrica: [null as number | null],
    boardEuropeAfricaPct: [null as number | null],
    boardOtherRegion: [null as number | null],
    boardOtherRegionPct: [null as number | null],
    boardWhites: [null as number | null],
    boardWhitesPct: [null as number | null],
    boardBlacks: [null as number | null],
    boardBlacksPct: [null as number | null],
    boardAsian: [null as number | null],
    boardAsianPct: [null as number | null],
    boardHispanic: [null as number | null],
    boardHispanicPct: [null as number | null],
    boardOtherMinority: [null as number | null],
    boardOtherMinorityPct: [null as number | null],

    // Demographics - Leadership
    leadershipMale: [null as number | null],
    leadershipMalePct: [null as number | null],
    leadershipFemale: [null as number | null],
    leadershipFemalePct: [null as number | null],
    leadershipLgbt: [null as number | null],
    leadershipLgbtPct: [null as number | null],
    leadershipUnderThirty: [null as number | null],
    leadershipUnderThirtyPct: [null as number | null],
    leadershipOverThirtyUnderFifty: [null as number | null],
    leadershipOverThirtyUnderFiftyPct: [null as number | null],
    leadershipOverFifty: [null as number | null],
    leadershipOverFiftyPct: [null as number | null],
    leadershipNonClassified: [null as number | null],
    leadershipNonClassifiedPct: [null as number | null],
    leadershipAmerica: [null as number | null],
    leadershipAmericaPct: [null as number | null],
    leadershipAsiaPacific: [null as number | null],
    leadershipAsiaPacificPct: [null as number | null],
    leadershipEuropeAfrica: [null as number | null],
    leadershipEuropeAfricaPct: [null as number | null],
    leadershipOtherRegion: [null as number | null],
    leadershipOtherRegionPct: [null as number | null],
    leadershipWhites: [null as number | null],
    leadershipWhitesPct: [null as number | null],
    leadershipBlacks: [null as number | null],
    leadershipBlacksPct: [null as number | null],
    leadershipAsian: [null as number | null],
    leadershipAsianPct: [null as number | null],
    leadershipHispanic: [null as number | null],
    leadershipHispanicPct: [null as number | null],
    leadershipOtherMinority: [null as number | null],
    leadershipOtherMinorityPct: [null as number | null],

    // Demographics - Employees
    employeesMale: [null as number | null],
    employeesMalePct: [null as number | null],
    employeesFemale: [null as number | null],
    employeesFemalePct: [null as number | null],
    employeesLgbt: [null as number | null],
    employeesLgbtPct: [null as number | null],
    employeesUnderThirty: [null as number | null],
    employeesUnderThirtyPct: [null as number | null],
    employeesOverThirtyUnderFifty: [null as number | null],
    employeesOverThirtyUnderFiftyPct: [null as number | null],
    employeesOverFifty: [null as number | null],
    employeesOverFiftyPct: [null as number | null],
    employeesNonClassified: [null as number | null],
    employeesNonClassifiedPct: [null as number | null],
    employeesAmerica: [null as number | null],
    employeesAmericaPct: [null as number | null],
    employeesAsiaPacific: [null as number | null],
    employeesAsiaPacificPct: [null as number | null],
    employeesEuropeAfrica: [null as number | null],
    employeesEuropeAfricaPct: [null as number | null],
    employeesOtherRegion: [null as number | null],
    employeesOtherRegionPct: [null as number | null],
    employeesWhites: [null as number | null],
    employeesWhitesPct: [null as number | null],
    employeesBlacks: [null as number | null],
    employeesBlacksPct: [null as number | null],
    employeesAsian: [null as number | null],
    employeesAsianPct: [null as number | null],
    employeesHispanic: [null as number | null],
    employeesHispanicPct: [null as number | null],
    employeesOtherMinority: [null as number | null],
    employeesOtherMinorityPct: [null as number | null],

    // Right sidebar
    averageAge: [null as number | null],
    leadershipTotal: [null as number | null],
    disabledEmployees: [null as number | null],
    disabledEmployeesPct: [null as number | null],
    genderPayGap: [null as number | null],
    averageMaleSalary: [null as number | null],
    averageMaleSalaryCurrencyId: [''],
    averageFemaleSalary: [null as number | null],
    averageFemaleSalaryCurrencyId: [''],

    // Parental Leave
    entitledToParentalLeave: [null as number | null],
    tookToParentalLeave: [null as number | null],
    returnedFromParentalLeave: [null as number | null],

    // Training & Development
    trainingCosts: [null as number | null],
    trainingCostsCurrencyId: [''],
    workingHours: [null as number | null],
    employeeHealthTrainingHours: [null as number | null],
    trainingEmployees: [null as number | null],
    trainingHoursPerEmployee: [null as number | null],
    tradeUnionRepresentationRate: [null as number | null],
    collectiveAgreementRate: [null as number | null],

    // Comments
    comments: [''],
    keywords: this.fb.group({
      hires: [''],
      health: [''],
      averageAge: [''],
      leadership: [''],
      disabled: [''],
      genderGap: [''],
      parentalLeave: [''],
      training: [''],
      tradeUnion: [''],
      collective: ['']
    })
  });

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  /**
   * An Other Measurement figure, written the way NumberFormatDirective writes the input it sits
   * under: `decimals` places when the input fixes them, the locale's own (up to three) otherwise.
   * Defaulting to none rounded every percentage and rate of the other year to a whole number.
   */
  formatNumber(value: number | null | undefined, decimals: number | null = null): string {
    if (value == null) return '';
    const opts: Intl.NumberFormatOptions = decimals != null
      ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
      : {};
    return value.toLocaleString('de-DE', opts);
  }

  /**
   * Every row of the demographics sub-tables. LGBT is one of the three By Gender rows, not an
   * extra beside them: the people counted there are counted nowhere else in the sub-table, so
   * Male, Female and LGBT are what the sub-table's total divides into.
   */
  private readonly demoSuffixes = [
    'Male', 'Female', 'Lgbt', 'UnderThirty', 'OverThirtyUnderFifty', 'OverFifty', 'NonClassified',
    'America', 'AsiaPacific', 'EuropeAfrica', 'OtherRegion',
    'Whites', 'Blacks', 'Asian', 'Hispanic', 'OtherMinority'
  ];

  readonly demoPctSuffixes = {
    gender: ['Male', 'Female', 'Lgbt'],
    age: ['UnderThirty', 'OverThirtyUnderFifty', 'OverFifty', 'NonClassified'],
    region: ['America', 'AsiaPacific', 'EuropeAfrica', 'OtherRegion'],
    minority: ['Whites', 'Blacks', 'Asian', 'Hispanic', 'OtherMinority']
  };

  sumDemoPct(prefix: string, suffixes: string[]): number {
    let sum = 0;
    for (const s of suffixes) {
      const v = this.form.get(`${prefix}${s}Pct`)?.value;
      if (typeof v === 'number' && !isNaN(v)) sum += v;
    }
    return Math.round(sum * 100) / 100;
  }

  formatPct(value: number): string {
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  getOtherCurrencyName(currencyId: string | null | undefined): string {
    if (!currencyId) return '';
    return this.currencies.find(c => c.id === currencyId)?.name ?? '';
  }

  getTotalTypeLabel(typeValue: number | null | undefined): string {
    if (typeValue == null) return '';
    return this.totalTypes.find(t => t.value === typeValue)?.label ?? '';
  }

  private readonly keywordMapping: { formField: string; type: KeywordType }[] = [
    { formField: 'hires', type: KeywordType.EmployeesHires },
    { formField: 'health', type: KeywordType.EmployeesHealth },
    { formField: 'averageAge', type: KeywordType.EmployeesAverageAge },
    { formField: 'leadership', type: KeywordType.EmployeesLeadership },
    { formField: 'disabled', type: KeywordType.EmployeesDisabled },
    { formField: 'genderGap', type: KeywordType.EmployeesGenderGap },
    { formField: 'parentalLeave', type: KeywordType.EmployeesParentalLeave },
    { formField: 'training', type: KeywordType.EmployeesTraining },
    { formField: 'tradeUnion', type: KeywordType.EmployeesTradeUnion },
    { formField: 'collective', type: KeywordType.EmployeesCollective },
  ];

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('employees'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'employees', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'employees', year: this.selectedYear }));
    }

    this.patchKeywords();
    this.setupCalculations();
    this.loadLookupData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  ngOnDestroy(): void {
    this.calcSubscriptions.unsubscribe();
  }

  private patchKeywords(): void {
    const patch: Record<string, string> = {};
    for (const mapping of this.keywordMapping) {
      const kw = this.keywords.find(k => k.type === mapping.type);
      patch[mapping.formField] = kw?.value ?? '';
    }
    this.form.get('keywords')!.patchValue(patch, { emitEvent: false });
    this.form.markAsPristine();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'employees', year }));
    this.loadData();
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;
    this.employeeMetricsService.get(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        this.otherData = resource.state === 'present' ? resource.data : null;
      },
      error: () => {
        this.otherData = null;
      }
    });
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasEmployeeChanges = this.form.dirty && !hasKeywordChanges;

    if (hasEmployeeChanges && hasKeywordChanges) return 'employee metrics data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'employee metrics data';
  }

  onSave(): void {
    if (!this.companyId) return;

    // No field is required: a company that publishes only percentages — with no Total No,
    // no sub-table total and no counts — still has its percentages saved as entered.
    this.performSave();
  }

  private performSave(): void {
    if (!this.companyId) return;

    const v = this.form.value;

    const request: UpsertEmployeeMetricsRequest = {
      year: parseInt(this.selectedYear),
      total: v.total ?? null,
      totalPct: v.totalPct ?? null,
      totalType: v.totalType ?? null,
      permanent: v.permanent ?? null,
      permanentPct: v.permanentPct ?? null,
      temporary: v.temporary ?? null,
      temporaryPct: v.temporaryPct ?? null,
      fullTime: v.fullTime ?? null,
      fullTimePct: v.fullTimePct ?? null,
      partTime: v.partTime ?? null,
      partTimePct: v.partTimePct ?? null,
      contractors: v.contractors ?? null,
      newEmployeeHires: v.newEmployeeHires ?? null,
      newEmployeeHiresPct: v.newEmployeeHiresPct ?? null,
      employeeTurnover: v.employeeTurnover ?? null,
      employeeTurnoverPct: v.employeeTurnoverPct ?? null,
      voluntaryTurnover: v.voluntaryTurnover ?? null,
      voluntaryTurnoverPct: v.voluntaryTurnoverPct ?? null,
      injuryRate: v.injuryRate ?? null,
      accidents: v.accidents ?? null,
      fatalities: v.fatalities ?? null,
      lostTimeIncidentRate: v.lostTimeIncidentRate ?? null,
      totalRecordableIncidentRate: v.totalRecordableIncidentRate ?? null,
      lostWorkingDays: v.lostWorkingDays ?? null,
      absenteeRate: v.absenteeRate ?? null,
      contractorsInjuryRate: v.contractorsInjuryRate ?? null,
      contractorsAccidents: v.contractorsAccidents ?? null,
      contractorsFatalities: v.contractorsFatalities ?? null,
      contractorsLostTimeIncidentRate: v.contractorsLostTimeIncidentRate ?? null,
      contractorsTotalRecordableIncidentRate: v.contractorsTotalRecordableIncidentRate ?? null,
      contractorsLostWorkingDays: v.contractorsLostWorkingDays ?? null,
      contractorsAbsenteeRate: v.contractorsAbsenteeRate ?? null,
      totalAccidents: v.totalAccidents ?? null,
      totalFatalities: v.totalFatalities ?? null,
      totalLostWorkingDays: v.totalLostWorkingDays ?? null,
      newHiresMale: v.newHiresMale ?? null,
      newHiresMalePct: v.newHiresMalePct ?? null,
      newHiresFemale: v.newHiresFemale ?? null,
      newHiresFemalePct: v.newHiresFemalePct ?? null,
      newHiresLgbt: v.newHiresLgbt ?? null,
      newHiresLgbtPct: v.newHiresLgbtPct ?? null,
      newHiresUnderThirty: v.newHiresUnderThirty ?? null,
      newHiresUnderThirtyPct: v.newHiresUnderThirtyPct ?? null,
      newHiresOverThirtyUnderFifty: v.newHiresOverThirtyUnderFifty ?? null,
      newHiresOverThirtyUnderFiftyPct: v.newHiresOverThirtyUnderFiftyPct ?? null,
      newHiresOverFifty: v.newHiresOverFifty ?? null,
      newHiresOverFiftyPct: v.newHiresOverFiftyPct ?? null,
      newHiresNonClassified: v.newHiresNonClassified ?? null,
      newHiresNonClassifiedPct: v.newHiresNonClassifiedPct ?? null,
      newHiresAmerica: v.newHiresAmerica ?? null,
      newHiresAmericaPct: v.newHiresAmericaPct ?? null,
      newHiresAsiaPacific: v.newHiresAsiaPacific ?? null,
      newHiresAsiaPacificPct: v.newHiresAsiaPacificPct ?? null,
      newHiresEuropeAfrica: v.newHiresEuropeAfrica ?? null,
      newHiresEuropeAfricaPct: v.newHiresEuropeAfricaPct ?? null,
      newHiresOtherRegion: v.newHiresOtherRegion ?? null,
      newHiresOtherRegionPct: v.newHiresOtherRegionPct ?? null,
      newHiresWhites: v.newHiresWhites ?? null,
      newHiresWhitesPct: v.newHiresWhitesPct ?? null,
      newHiresBlacks: v.newHiresBlacks ?? null,
      newHiresBlacksPct: v.newHiresBlacksPct ?? null,
      newHiresAsian: v.newHiresAsian ?? null,
      newHiresAsianPct: v.newHiresAsianPct ?? null,
      newHiresHispanic: v.newHiresHispanic ?? null,
      newHiresHispanicPct: v.newHiresHispanicPct ?? null,
      newHiresOtherMinority: v.newHiresOtherMinority ?? null,
      newHiresOtherMinorityPct: v.newHiresOtherMinorityPct ?? null,
      turnoverMale: v.turnoverMale ?? null,
      turnoverMalePct: v.turnoverMalePct ?? null,
      turnoverFemale: v.turnoverFemale ?? null,
      turnoverFemalePct: v.turnoverFemalePct ?? null,
      turnoverLgbt: v.turnoverLgbt ?? null,
      turnoverLgbtPct: v.turnoverLgbtPct ?? null,
      turnoverUnderThirty: v.turnoverUnderThirty ?? null,
      turnoverUnderThirtyPct: v.turnoverUnderThirtyPct ?? null,
      turnoverOverThirtyUnderFifty: v.turnoverOverThirtyUnderFifty ?? null,
      turnoverOverThirtyUnderFiftyPct: v.turnoverOverThirtyUnderFiftyPct ?? null,
      turnoverOverFifty: v.turnoverOverFifty ?? null,
      turnoverOverFiftyPct: v.turnoverOverFiftyPct ?? null,
      turnoverNonClassified: v.turnoverNonClassified ?? null,
      turnoverNonClassifiedPct: v.turnoverNonClassifiedPct ?? null,
      turnoverAmerica: v.turnoverAmerica ?? null,
      turnoverAmericaPct: v.turnoverAmericaPct ?? null,
      turnoverAsiaPacific: v.turnoverAsiaPacific ?? null,
      turnoverAsiaPacificPct: v.turnoverAsiaPacificPct ?? null,
      turnoverEuropeAfrica: v.turnoverEuropeAfrica ?? null,
      turnoverEuropeAfricaPct: v.turnoverEuropeAfricaPct ?? null,
      turnoverOtherRegion: v.turnoverOtherRegion ?? null,
      turnoverOtherRegionPct: v.turnoverOtherRegionPct ?? null,
      turnoverWhites: v.turnoverWhites ?? null,
      turnoverWhitesPct: v.turnoverWhitesPct ?? null,
      turnoverBlacks: v.turnoverBlacks ?? null,
      turnoverBlacksPct: v.turnoverBlacksPct ?? null,
      turnoverAsian: v.turnoverAsian ?? null,
      turnoverAsianPct: v.turnoverAsianPct ?? null,
      turnoverHispanic: v.turnoverHispanic ?? null,
      turnoverHispanicPct: v.turnoverHispanicPct ?? null,
      turnoverOtherMinority: v.turnoverOtherMinority ?? null,
      turnoverOtherMinorityPct: v.turnoverOtherMinorityPct ?? null,
      boardMale: v.boardMale ?? null,
      boardMalePct: v.boardMalePct ?? null,
      boardFemale: v.boardFemale ?? null,
      boardFemalePct: v.boardFemalePct ?? null,
      boardLgbt: v.boardLgbt ?? null,
      boardLgbtPct: v.boardLgbtPct ?? null,
      boardUnderThirty: v.boardUnderThirty ?? null,
      boardUnderThirtyPct: v.boardUnderThirtyPct ?? null,
      boardOverThirtyUnderFifty: v.boardOverThirtyUnderFifty ?? null,
      boardOverThirtyUnderFiftyPct: v.boardOverThirtyUnderFiftyPct ?? null,
      boardOverFifty: v.boardOverFifty ?? null,
      boardOverFiftyPct: v.boardOverFiftyPct ?? null,
      boardNonClassified: v.boardNonClassified ?? null,
      boardNonClassifiedPct: v.boardNonClassifiedPct ?? null,
      boardAmerica: v.boardAmerica ?? null,
      boardAmericaPct: v.boardAmericaPct ?? null,
      boardAsiaPacific: v.boardAsiaPacific ?? null,
      boardAsiaPacificPct: v.boardAsiaPacificPct ?? null,
      boardEuropeAfrica: v.boardEuropeAfrica ?? null,
      boardEuropeAfricaPct: v.boardEuropeAfricaPct ?? null,
      boardOtherRegion: v.boardOtherRegion ?? null,
      boardOtherRegionPct: v.boardOtherRegionPct ?? null,
      boardWhites: v.boardWhites ?? null,
      boardWhitesPct: v.boardWhitesPct ?? null,
      boardBlacks: v.boardBlacks ?? null,
      boardBlacksPct: v.boardBlacksPct ?? null,
      boardAsian: v.boardAsian ?? null,
      boardAsianPct: v.boardAsianPct ?? null,
      boardHispanic: v.boardHispanic ?? null,
      boardHispanicPct: v.boardHispanicPct ?? null,
      boardOtherMinority: v.boardOtherMinority ?? null,
      boardOtherMinorityPct: v.boardOtherMinorityPct ?? null,
      leadershipMale: v.leadershipMale ?? null,
      leadershipMalePct: v.leadershipMalePct ?? null,
      leadershipFemale: v.leadershipFemale ?? null,
      leadershipFemalePct: v.leadershipFemalePct ?? null,
      leadershipLgbt: v.leadershipLgbt ?? null,
      leadershipLgbtPct: v.leadershipLgbtPct ?? null,
      leadershipUnderThirty: v.leadershipUnderThirty ?? null,
      leadershipUnderThirtyPct: v.leadershipUnderThirtyPct ?? null,
      leadershipOverThirtyUnderFifty: v.leadershipOverThirtyUnderFifty ?? null,
      leadershipOverThirtyUnderFiftyPct: v.leadershipOverThirtyUnderFiftyPct ?? null,
      leadershipOverFifty: v.leadershipOverFifty ?? null,
      leadershipOverFiftyPct: v.leadershipOverFiftyPct ?? null,
      leadershipNonClassified: v.leadershipNonClassified ?? null,
      leadershipNonClassifiedPct: v.leadershipNonClassifiedPct ?? null,
      leadershipAmerica: v.leadershipAmerica ?? null,
      leadershipAmericaPct: v.leadershipAmericaPct ?? null,
      leadershipAsiaPacific: v.leadershipAsiaPacific ?? null,
      leadershipAsiaPacificPct: v.leadershipAsiaPacificPct ?? null,
      leadershipEuropeAfrica: v.leadershipEuropeAfrica ?? null,
      leadershipEuropeAfricaPct: v.leadershipEuropeAfricaPct ?? null,
      leadershipOtherRegion: v.leadershipOtherRegion ?? null,
      leadershipOtherRegionPct: v.leadershipOtherRegionPct ?? null,
      leadershipWhites: v.leadershipWhites ?? null,
      leadershipWhitesPct: v.leadershipWhitesPct ?? null,
      leadershipBlacks: v.leadershipBlacks ?? null,
      leadershipBlacksPct: v.leadershipBlacksPct ?? null,
      leadershipAsian: v.leadershipAsian ?? null,
      leadershipAsianPct: v.leadershipAsianPct ?? null,
      leadershipHispanic: v.leadershipHispanic ?? null,
      leadershipHispanicPct: v.leadershipHispanicPct ?? null,
      leadershipOtherMinority: v.leadershipOtherMinority ?? null,
      leadershipOtherMinorityPct: v.leadershipOtherMinorityPct ?? null,
      employeesMale: v.employeesMale ?? null,
      employeesMalePct: v.employeesMalePct ?? null,
      employeesFemale: v.employeesFemale ?? null,
      employeesFemalePct: v.employeesFemalePct ?? null,
      employeesLgbt: v.employeesLgbt ?? null,
      employeesLgbtPct: v.employeesLgbtPct ?? null,
      employeesUnderThirty: v.employeesUnderThirty ?? null,
      employeesUnderThirtyPct: v.employeesUnderThirtyPct ?? null,
      employeesOverThirtyUnderFifty: v.employeesOverThirtyUnderFifty ?? null,
      employeesOverThirtyUnderFiftyPct: v.employeesOverThirtyUnderFiftyPct ?? null,
      employeesOverFifty: v.employeesOverFifty ?? null,
      employeesOverFiftyPct: v.employeesOverFiftyPct ?? null,
      employeesNonClassified: v.employeesNonClassified ?? null,
      employeesNonClassifiedPct: v.employeesNonClassifiedPct ?? null,
      employeesAmerica: v.employeesAmerica ?? null,
      employeesAmericaPct: v.employeesAmericaPct ?? null,
      employeesAsiaPacific: v.employeesAsiaPacific ?? null,
      employeesAsiaPacificPct: v.employeesAsiaPacificPct ?? null,
      employeesEuropeAfrica: v.employeesEuropeAfrica ?? null,
      employeesEuropeAfricaPct: v.employeesEuropeAfricaPct ?? null,
      employeesOtherRegion: v.employeesOtherRegion ?? null,
      employeesOtherRegionPct: v.employeesOtherRegionPct ?? null,
      employeesWhites: v.employeesWhites ?? null,
      employeesWhitesPct: v.employeesWhitesPct ?? null,
      employeesBlacks: v.employeesBlacks ?? null,
      employeesBlacksPct: v.employeesBlacksPct ?? null,
      employeesAsian: v.employeesAsian ?? null,
      employeesAsianPct: v.employeesAsianPct ?? null,
      employeesHispanic: v.employeesHispanic ?? null,
      employeesHispanicPct: v.employeesHispanicPct ?? null,
      employeesOtherMinority: v.employeesOtherMinority ?? null,
      employeesOtherMinorityPct: v.employeesOtherMinorityPct ?? null,
      averageAge: v.averageAge ?? null,
      leadershipTotal: v.leadershipTotal ?? null,
      disabledEmployees: v.disabledEmployees ?? null,
      disabledEmployeesPct: v.disabledEmployeesPct ?? null,
      genderPayGap: v.genderPayGap ?? null,
      averageMaleSalary: v.averageMaleSalary ?? null,
      averageMaleSalaryCurrencyId: v.averageMaleSalaryCurrencyId || null,
      averageFemaleSalary: v.averageFemaleSalary ?? null,
      averageFemaleSalaryCurrencyId: v.averageFemaleSalaryCurrencyId || null,
      entitledToParentalLeave: v.entitledToParentalLeave ?? null,
      tookToParentalLeave: v.tookToParentalLeave ?? null,
      returnedFromParentalLeave: v.returnedFromParentalLeave ?? null,
      trainingCosts: v.trainingCosts ?? null,
      trainingCostsCurrencyId: v.trainingCostsCurrencyId || null,
      workingHours: v.workingHours ?? null,
      employeeHealthTrainingHours: v.employeeHealthTrainingHours ?? null,
      trainingEmployees: v.trainingEmployees ?? null,
      trainingHoursPerEmployee: v.trainingHoursPerEmployee ?? null,
      tradeUnionRepresentationRate: v.tradeUnionRepresentationRate ?? null,
      collectiveAgreementRate: v.collectiveAgreementRate ?? null,
      comments: v.comments || null
    };

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, this.keywordMapping, this.keywords);

    const hasEmployeeData = this.isCarriedForward || (this.form.dirty && Object.keys(v).some(k => k !== 'keywords' && this.form.get(k)?.dirty));
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => this.keywordMapping.some(m => m.type === k.type)
    );

    if (!hasEmployeeData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();

    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasEmployeeData
      ? this.employeeMetricsService.upsert(this.companyId!, request)
      : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_employeeResult, keywordsResult]) => {
        // The year has a record of its own now. Left set, the flag kept Save enabled after the
        // save went through, until the tab was reloaded.
        this.isCarriedForward = false;
        this.showYearWarning = false;
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private loadLookupData(): void {
    this.spinnerService.show();

    this.lutService.getCurrencies().subscribe({
      next: (currencies) => {
        this.currencies = currencies.sort((a, b) => a.name.localeCompare(b.name));
        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load lookup data');
        console.error('Lookup data error:', err);
      }
    });
  }

  private loadData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.show();
    this.employeeMetricsService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const formValues = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isCarriedForward = resource.state === 'carried-forward';
        this.showYearWarning = this.isCarriedForward || resource.state === 'absent';
        if (formValues) {
          this.patchForm(formValues);
        } else {
          // Nothing saved for the year: the amounts are still quoted in the company's
          // country currency, which the API hands over beside the empty envelope.
          this.resetForm(resource.context?.defaultCurrencyId ?? '');
        }
        this.loadBoardCompositionData();
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        this.isCarriedForward = false;
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading employee metrics data');
        }

        this.loadBoardCompositionData();
        console.error('Employee metrics error:', error);
      }
    });
  }

  private loadBoardCompositionData(): void {
    if (!this.companyId) return;

    this.boardService.getAll(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        // Members live in context — populated regardless of envelope state.
        const members = resource.context?.boardMembers ?? [];
        this.boardMemberCount = members.length > 0 ? members.length : null;
        // Re-derive Region/Minority % from any manually-entered Numbers now that the count is known.
        this.resyncBoardDemos();
        if (members.length === 0) return;

        const year = parseInt(this.selectedYear);
        const total = members.length;

        // By Gender (1 = Male, 2 = Female)
        const genderNum = (m: BoardMemberDto) => Number(m.directorGender);
        const male = members.filter(m => genderNum(m) === 1).length;
        const female = members.filter(m => genderNum(m) === 2).length;

        // By Age Group
        let underThirty = 0;
        let thirtyToFifty = 0;
        let overFifty = 0;
        let nonClassified = 0;

        for (const m of members) {
          if (m.directorYearOfBirth == null) {
            nonClassified++;
            continue;
          }
          const age = year - m.directorYearOfBirth;
          if (age < 30) underThirty++;
          else if (age <= 50) thirtyToFifty++;
          else overFifty++;
        }

        const pct = (n: number) => Math.round((n * 100 / total) * 100) / 100;

        this.form.patchValue({
          boardMale: male, boardMalePct: pct(male),
          boardFemale: female, boardFemalePct: pct(female),
          boardUnderThirty: underThirty, boardUnderThirtyPct: pct(underThirty),
          boardOverThirtyUnderFifty: thirtyToFifty, boardOverThirtyUnderFiftyPct: pct(thirtyToFifty),
          boardOverFifty: overFifty, boardOverFiftyPct: pct(overFifty),
          boardNonClassified: nonClassified, boardNonClassifiedPct: pct(nonClassified),
        }, { emitEvent: false });

        this.form.markAsPristine();
      },
      error: () => {
        // Board data not available — leave fields as-is
      }
    });
  }

  private calcPct(numerator: number | null, denominator: number | null): number | null {
    if (numerator == null || denominator == null || denominator === 0) return null;
    return Math.round((numerator * 100 / denominator) * 100) / 100;
  }

  private safeSum(...values: (number | null)[]): number | null {
    const nums = values.filter((v): v is number => v != null);
    return nums.length > 0 ? nums.reduce((a, b) => a + b, 0) : null;
  }

  private getBase(): number | null {
    const type = this.form.get('totalType')!.value;
    if (type === 0) return this.form.get('total')!.value;
    if (type === 1) return this.form.get('permanent')!.value;
    if (type === 2) return this.form.get('fullTime')!.value;
    return null;
  }

  private setupCalculations(): void {
    const f = this.form;
    const ctrl = (name: string) => f.get(name)!;
    const patchCalc = (name: string, value: number | null) => {
      ctrl(name).setValue(value, { emitEvent: false });
    };

    // Group A — Employee Totals Percentages
    // totalPct hardcoded to 100
    patchCalc('totalPct', 100);

    // permanent + temporary = total; auto-fill the counterpart when one is edited
    const permTempFields: Array<'permanent' | 'temporary' | 'permanentPct' | 'temporaryPct'> =
      ['permanent', 'temporary', 'permanentPct', 'temporaryPct'];
    for (const field of permTempFields) {
      this.calcSubscriptions.add(
        ctrl(field).valueChanges.subscribe(() => {
          this.permTempLastEdited = field;
          this.syncPair('permanent', 'temporary', field);
        })
      );
    }

    // fullTime + partTime = total; auto-fill the counterpart when one is edited
    const fullPartFields: Array<'fullTime' | 'partTime' | 'fullTimePct' | 'partTimePct'> =
      ['fullTime', 'partTime', 'fullTimePct', 'partTimePct'];
    for (const field of fullPartFields) {
      this.calcSubscriptions.add(
        ctrl(field).valueChanges.subscribe(() => {
          this.fullPartLastEdited = field;
          this.syncPair('fullTime', 'partTime', field);
        })
      );
    }

    // On total change, re-sync both pairs based on whichever side was last edited
    this.calcSubscriptions.add(
      ctrl('total').valueChanges.subscribe(() => {
        this.syncPair('permanent', 'temporary', 'total', this.permTempLastEdited);
        this.syncPair('fullTime', 'partTime', 'total', this.fullPartLastEdited);
      })
    );

    // Group B — Hires / Turnover / Voluntary Turnover: each number ↔ pct is bidirectional,
    // denominator = base per totalType. Changing totalType does NOT trigger recalc.
    const baseFieldSetup = (
      numField: string,
      setLastEdited: (v: 'num' | 'pct') => void,
    ) => {
      const pctField = `${numField}Pct`;
      this.calcSubscriptions.add(
        ctrl(numField).valueChanges.subscribe(() => {
          setLastEdited('num');
          this.syncBaseField(numField, 'num');
        })
      );
      this.calcSubscriptions.add(
        ctrl(pctField).valueChanges.subscribe(() => {
          setLastEdited('pct');
          this.syncBaseField(numField, 'pct');
        })
      );
    };
    baseFieldSetup('newEmployeeHires', v => {
      this.hiresLastEdited = v === 'num' ? 'newEmployeeHires' : 'newEmployeeHiresPct';
    });
    baseFieldSetup('employeeTurnover', v => {
      this.turnoverLastEdited = v === 'num' ? 'employeeTurnover' : 'employeeTurnoverPct';
    });
    baseFieldSetup('voluntaryTurnover', v => {
      this.voluntaryLastEdited = v === 'num' ? 'voluntaryTurnover' : 'voluntaryTurnoverPct';
    });
    this.calcSubscriptions.add(
      merge(
        ctrl('total').valueChanges,
        ctrl('permanent').valueChanges,
        ctrl('fullTime').valueChanges
      ).subscribe(() => {
        this.syncBaseField('newEmployeeHires', this.hiresLastEdited?.endsWith('Pct') ? 'pct' : 'num');
        this.syncBaseField('employeeTurnover', this.turnoverLastEdited?.endsWith('Pct') ? 'pct' : 'num');
        this.syncBaseField('voluntaryTurnover', this.voluntaryLastEdited?.endsWith('Pct') ? 'pct' : 'num');
      })
    );

    // Group C — Health & Safety Totals: auto-sum unless user manually overrides the total
    type HsKey = 'accidents' | 'fatalities' | 'lostWorkingDays';
    const hsTotalPairs: Array<{ total: string; a: string; b: string; key: HsKey }> = [
      { total: 'totalAccidents', a: 'accidents', b: 'contractorsAccidents', key: 'accidents' },
      { total: 'totalFatalities', a: 'fatalities', b: 'contractorsFatalities', key: 'fatalities' },
      { total: 'totalLostWorkingDays', a: 'lostWorkingDays', b: 'contractorsLostWorkingDays', key: 'lostWorkingDays' }
    ];
    for (const { total, a, b, key } of hsTotalPairs) {
      this.calcSubscriptions.add(
        merge(ctrl(a).valueChanges, ctrl(b).valueChanges).subscribe(() => {
          this.hsTotalManual[key] = false;
          patchCalc(total, this.safeSum(ctrl(a).value, ctrl(b).value));
        })
      );
      this.calcSubscriptions.add(
        ctrl(total).valueChanges.subscribe(() => {
          this.hsTotalManual[key] = true;
        })
      );
    }

    // Group D — Disabled Employees (denominator = total); bidirectional
    this.calcSubscriptions.add(
      ctrl('disabledEmployees').valueChanges.subscribe(() => {
        this.disabledLastEdited = 'num';
        this.syncBaseField('disabledEmployees', 'num', 'total');
      })
    );
    this.calcSubscriptions.add(
      ctrl('disabledEmployeesPct').valueChanges.subscribe(() => {
        this.disabledLastEdited = 'pct';
        this.syncBaseField('disabledEmployees', 'pct', 'total');
      })
    );
    this.calcSubscriptions.add(
      ctrl('total').valueChanges.subscribe(() => {
        const side = this.disabledLastEdited === 'pct' ? 'pct' : 'num';
        this.syncBaseField('disabledEmployees', side, 'total');
      })
    );

    // Group E — Demographics New Hires (denominator = newEmployeeHires); bidirectional
    for (const suffix of this.demoSuffixes) {
      const numField = `newHires${suffix}`;
      const pctField = `newHires${suffix}Pct`;
      this.calcSubscriptions.add(
        ctrl(numField).valueChanges.subscribe(() => {
          this.newHiresDemoLastEdited[suffix] = 'num';
          this.syncBaseField(numField, 'num', 'newEmployeeHires');
        })
      );
      this.calcSubscriptions.add(
        ctrl(pctField).valueChanges.subscribe(() => {
          this.newHiresDemoLastEdited[suffix] = 'pct';
          this.syncBaseField(numField, 'pct', 'newEmployeeHires');
        })
      );
    }
    this.calcSubscriptions.add(
      ctrl('newEmployeeHires').valueChanges.subscribe(() => {
        for (const suffix of this.demoSuffixes) {
          const side = this.newHiresDemoLastEdited[suffix] === 'pct' ? 'pct' : 'num';
          this.syncBaseField(`newHires${suffix}`, side, 'newEmployeeHires');
        }
      })
    );

    // Group F — Demographics Turnover (denominator = employeeTurnover); bidirectional
    for (const suffix of this.demoSuffixes) {
      const numField = `turnover${suffix}`;
      const pctField = `turnover${suffix}Pct`;
      this.calcSubscriptions.add(
        ctrl(numField).valueChanges.subscribe(() => {
          this.turnoverDemoLastEdited[suffix] = 'num';
          this.syncBaseField(numField, 'num', 'employeeTurnover');
        })
      );
      this.calcSubscriptions.add(
        ctrl(pctField).valueChanges.subscribe(() => {
          this.turnoverDemoLastEdited[suffix] = 'pct';
          this.syncBaseField(numField, 'pct', 'employeeTurnover');
        })
      );
    }
    this.calcSubscriptions.add(
      ctrl('employeeTurnover').valueChanges.subscribe(() => {
        for (const suffix of this.demoSuffixes) {
          const side = this.turnoverDemoLastEdited[suffix] === 'pct' ? 'pct' : 'num';
          this.syncBaseField(`turnover${suffix}`, side, 'employeeTurnover');
        }
      })
    );

    // Group G — Board demographics (denominator = board member count from Board tab); bidirectional
    for (const suffix of this.demoSuffixes) {
      const numField = `board${suffix}`;
      const pctField = `board${suffix}Pct`;
      this.calcSubscriptions.add(
        ctrl(numField).valueChanges.subscribe(() => this.syncBoardField(suffix, 'num'))
      );
      this.calcSubscriptions.add(
        ctrl(pctField).valueChanges.subscribe(() => this.syncBoardField(suffix, 'pct'))
      );
    }

    // Group H — Demographics Leadership (denominator = leadershipTotal); bidirectional
    for (const suffix of this.demoSuffixes) {
      const numField = `leadership${suffix}`;
      const pctField = `leadership${suffix}Pct`;
      this.calcSubscriptions.add(
        ctrl(numField).valueChanges.subscribe(() => {
          this.leadershipDemoLastEdited[suffix] = 'num';
          this.syncBaseField(numField, 'num', 'leadershipTotal');
        })
      );
      this.calcSubscriptions.add(
        ctrl(pctField).valueChanges.subscribe(() => {
          this.leadershipDemoLastEdited[suffix] = 'pct';
          this.syncBaseField(numField, 'pct', 'leadershipTotal');
        })
      );
    }
    this.calcSubscriptions.add(
      ctrl('leadershipTotal').valueChanges.subscribe(() => {
        for (const suffix of this.demoSuffixes) {
          const side = this.leadershipDemoLastEdited[suffix] === 'pct' ? 'pct' : 'num';
          this.syncBaseField(`leadership${suffix}`, side, 'leadershipTotal');
        }
      })
    );

    // Group I — Demographics Employees (denominator = getBase() per totalType); bidirectional
    for (const suffix of this.demoSuffixes) {
      const numField = `employees${suffix}`;
      const pctField = `employees${suffix}Pct`;
      this.calcSubscriptions.add(
        ctrl(numField).valueChanges.subscribe(() => {
          this.employeesDemoLastEdited[suffix] = 'num';
          this.syncBaseField(numField, 'num');
        })
      );
      this.calcSubscriptions.add(
        ctrl(pctField).valueChanges.subscribe(() => {
          this.employeesDemoLastEdited[suffix] = 'pct';
          this.syncBaseField(numField, 'pct');
        })
      );
    }
    this.calcSubscriptions.add(
      merge(
        ctrl('total').valueChanges,
        ctrl('permanent').valueChanges,
        ctrl('fullTime').valueChanges
      ).subscribe(() => {
        for (const suffix of this.demoSuffixes) {
          const side = this.employeesDemoLastEdited[suffix] === 'pct' ? 'pct' : 'num';
          this.syncBaseField(`employees${suffix}`, side);
        }
      })
    );

    // By Gender: Male + Female + LGBT = 100% across each analysis column
    const genderPrefixes: Array<{ prefix: string; hasBase: boolean }> = [
      { prefix: 'newHires', hasBase: true },
      { prefix: 'turnover', hasBase: true },
      { prefix: 'board', hasBase: false },
      { prefix: 'leadership', hasBase: true },
      { prefix: 'employees', hasBase: true }
    ];
    for (const { prefix, hasBase } of genderPrefixes) {
      this.calcSubscriptions.add(
        ctrl(`${prefix}MalePct`).valueChanges.subscribe(() => this.syncGenderSplit(prefix, 'Male'))
      );
      this.calcSubscriptions.add(
        ctrl(`${prefix}FemalePct`).valueChanges.subscribe(() => this.syncGenderSplit(prefix, 'Female'))
      );
      // An LGBT share changes what is left for the other two, so the split is rebuilt around it —
      // leaving alone whichever gender the user drove last.
      this.calcSubscriptions.add(
        ctrl(`${prefix}LgbtPct`).valueChanges.subscribe(() => this.syncGenderSplit(prefix, 'Lgbt'))
      );
      if (hasBase) {
        this.calcSubscriptions.add(
          ctrl(`${prefix}Male`).valueChanges.subscribe(() => this.syncGenderSplit(prefix, 'Male'))
        );
        this.calcSubscriptions.add(
          ctrl(`${prefix}Female`).valueChanges.subscribe(() => this.syncGenderSplit(prefix, 'Female'))
        );
        this.calcSubscriptions.add(
          ctrl(`${prefix}Lgbt`).valueChanges.subscribe(() => this.syncGenderSplit(prefix, 'Lgbt'))
        );
      }
    }
  }

  private syncPair(
    aField: string,
    bField: string,
    trigger: string,
    lastEdited: string | null = trigger
  ): void {
    const f = this.form;
    const aPctField = `${aField}Pct`;
    const bPctField = `${bField}Pct`;
    const total = f.get('total')!.value as number | null;
    const set = (name: string, value: number | null) =>
      f.get(name)!.setValue(value, { emitEvent: false });
    const round2 = (v: number): number => Math.round(v * 100) / 100;

    const effective = trigger === 'total' ? lastEdited : trigger;

    if (total != null) {
      if (effective === aField) {
        const a = f.get(aField)!.value as number | null;
        if (a != null) set(bField, Math.max(0, total - a));
      } else if (effective === bField) {
        const b = f.get(bField)!.value as number | null;
        if (b != null) set(aField, Math.max(0, total - b));
      } else if (effective === aPctField) {
        const aPct = f.get(aPctField)!.value as number | null;
        if (aPct != null) {
          const a = Math.round(total * aPct / 100);
          set(aField, a);
          set(bField, Math.max(0, total - a));
          set(bPctField, round2(100 - aPct));
          return;
        }
      } else if (effective === bPctField) {
        const bPct = f.get(bPctField)!.value as number | null;
        if (bPct != null) {
          const b = Math.round(total * bPct / 100);
          set(bField, b);
          set(aField, Math.max(0, total - b));
          set(aPctField, round2(100 - bPct));
          return;
        }
      }
    }

    if (total == null) {
      // No Total No to split. The two halves are still each other's complement, so a company
      // that publishes the split as percentages alone keeps both — nothing is derived, and
      // nothing already entered is cleared.
      const editedPct = effective === aPctField ? aPctField : (effective === bPctField ? bPctField : null);
      if (editedPct != null) {
        const pct = f.get(editedPct)!.value as number | null;
        if (pct != null) {
          const otherPctField = editedPct === aPctField ? bPctField : aPctField;
          set(otherPctField, Math.max(0, round2(100 - pct)));
        }
      }
      return;
    }

    const a = f.get(aField)!.value as number | null;
    const aPct = this.calcPct(a, total);
    set(aPctField, aPct);
    set(bPctField, aPct != null ? round2(100 - aPct) : null);
  }

  /**
   * Keeps a By Gender column adding up to its own total. Male, Female and LGBT are three shares
   * of one whole, so two of them are taken as the user gave them and the third is whatever is
   * left of 100%. Leaving LGBT out of that sum is what used to push the counts past the total:
   * the people recorded as LGBT were handed to the other gender a second time.
   *
   * @param edited the row just touched. 'Lgbt' gives way on the gender driven least recently,
   *               so the figure the user typed most recently is the one that survives.
   */
  private syncGenderSplit(prefix: string, edited: 'Male' | 'Female' | 'Lgbt'): void {
    const f = this.form;
    const pctOf = (suffix: string) => f.get(`${prefix}${suffix}Pct`)!.value as number | null;

    if (edited !== 'Lgbt') this.genderLastEdited[prefix] = edited;

    const residual: 'Male' | 'Female' =
      edited === 'Male' ? 'Female'
        : edited === 'Female' ? 'Male'
          : this.genderLastEdited[prefix] === 'Female' ? 'Male' : 'Female';
    const kept: 'Male' | 'Female' = residual === 'Male' ? 'Female' : 'Male';

    // Nothing to divide up until the gender being kept has a percentage of its own — clearing
    // one out must not hand the whole column to the other.
    const keptPct = pctOf(kept);
    if (keptPct == null) return;

    const residualPct = Math.max(0, Math.round((100 - keptPct - (pctOf('Lgbt') ?? 0)) * 100) / 100);
    f.get(`${prefix}${residual}Pct`)!.setValue(residualPct, { emitEvent: false });

    // All three are pct-driven from here, so a change of base keeps them adding up to 100.
    for (const gender of ['Male', 'Female', 'Lgbt']) {
      this.markDemoGenderPct(prefix, gender);
    }

    const base = this.getBaseValueForPrefix(prefix);
    if (base != null) {
      f.get(`${prefix}${residual}`)!.setValue(Math.round(base * residualPct / 100), { emitEvent: false });
    }
  }

  private getBaseValueForPrefix(prefix: string): number | null {
    const f = this.form;
    switch (prefix) {
      case 'newHires': return f.get('newEmployeeHires')!.value as number | null;
      case 'turnover': return f.get('employeeTurnover')!.value as number | null;
      case 'leadership': return f.get('leadershipTotal')!.value as number | null;
      case 'employees': return this.getBase();
      default: return null;
    }
  }

  private markDemoGenderPct(prefix: string, gender: string): void {
    switch (prefix) {
      case 'newHires': this.newHiresDemoLastEdited[gender] = 'pct'; break;
      case 'turnover': this.turnoverDemoLastEdited[gender] = 'pct'; break;
      case 'leadership': this.leadershipDemoLastEdited[gender] = 'pct'; break;
      case 'employees': this.employeesDemoLastEdited[gender] = 'pct'; break;
    }
  }

  private syncBaseField(numField: string, side: 'num' | 'pct', baseFieldName?: string): void {
    const f = this.form;
    const pctField = `${numField}Pct`;
    const base = baseFieldName
      ? (f.get(baseFieldName)!.value as number | null)
      : this.getBase();
    const set = (name: string, value: number | null) =>
      f.get(name)!.setValue(value, { emitEvent: false });

    if (side === 'pct') {
      const pct = f.get(pctField)!.value as number | null;
      if (base != null && pct != null) {
        set(numField, Math.round(base * pct / 100));
      }
    } else {
      // Without a denominator the percentage cannot be derived — leave whatever was entered
      // by hand rather than blanking it.
      if (base == null) return;
      const num = f.get(numField)!.value as number | null;
      set(pctField, this.calcPct(num, base));
    }
  }

  private syncBoardField(suffix: string, side: 'num' | 'pct'): void {
    const f = this.form;
    const numField = `board${suffix}`;
    const pctField = `${numField}Pct`;
    const base = this.boardMemberCount;
    const set = (name: string, value: number | null) =>
      f.get(name)!.setValue(value, { emitEvent: false });

    if (side === 'pct') {
      const pct = f.get(pctField)!.value as number | null;
      if (base != null && pct != null) {
        set(numField, Math.round(base * pct / 100));
      }
    } else {
      // No board roster for the year — keep the percentages that were entered by hand.
      if (base == null) return;
      const num = f.get(numField)!.value as number | null;
      set(pctField, this.calcPct(num, base));
    }
  }

  /**
   * Re-derives the board percentages from the Numbers now that the member count is known.
   * Only that direction: a percentage entered by hand stays as it is, and no Number is
   * invented for it — the roster the numbers come from is the Board tab's business.
   */
  private resyncBoardDemos(): void {
    for (const suffix of this.demoSuffixes) {
      const num = this.form.get(`board${suffix}`)!.value as number | null;
      const pct = this.calcPct(num, this.boardMemberCount);
      if (pct != null) {
        this.form.get(`board${suffix}Pct`)!.setValue(pct, { emitEvent: false });
      }
    }
  }

  private recalculateAll(): void {
    const f = this.form;
    const v = (name: string) => f.get(name)!.value;
    const set = (name: string, value: number | null) => {
      f.get(name)!.setValue(value, { emitEvent: false });
    };
    // A percentage is data in its own right, not a view of the numbers: it is only overwritten
    // by one we can actually derive. A company that reports percentages alone — no Total No, no
    // sub-table total, no counts — keeps what was saved for it.
    const setPct = (name: string, value: number | null) => {
      if (value == null) return;
      set(name, value);
    };
    const setComplementPct = (name: string, ofPct: number | null) => {
      if (ofPct == null) return;
      set(name, Math.round((100 - ofPct) * 100) / 100);
    };

    // Group A
    set('totalPct', 100);
    const permPct = this.calcPct(v('permanent'), v('total'));
    setPct('permanentPct', permPct);
    setComplementPct('temporaryPct', permPct);
    const ftPct = this.calcPct(v('fullTime'), v('total'));
    setPct('fullTimePct', ftPct);
    setComplementPct('partTimePct', ftPct);

    // Group B
    const base = this.getBase();
    setPct('newEmployeeHiresPct', this.calcPct(v('newEmployeeHires'), base));
    setPct('employeeTurnoverPct', this.calcPct(v('employeeTurnover'), base));
    setPct('voluntaryTurnoverPct', this.calcPct(v('voluntaryTurnover'), base));

    // Group C
    if (!this.hsTotalManual.accidents) {
      set('totalAccidents', this.safeSum(v('accidents'), v('contractorsAccidents')));
    }
    if (!this.hsTotalManual.fatalities) {
      set('totalFatalities', this.safeSum(v('fatalities'), v('contractorsFatalities')));
    }
    if (!this.hsTotalManual.lostWorkingDays) {
      set('totalLostWorkingDays', this.safeSum(v('lostWorkingDays'), v('contractorsLostWorkingDays')));
    }

    // Group D
    setPct('disabledEmployeesPct', this.calcPct(v('disabledEmployees'), v('total')));

    // Groups E, F, H, I
    for (const suffix of this.demoSuffixes) {
      setPct(`newHires${suffix}Pct`, this.calcPct(v(`newHires${suffix}`), v('newEmployeeHires')));
      setPct(`turnover${suffix}Pct`, this.calcPct(v(`turnover${suffix}`), v('employeeTurnover')));
      setPct(`leadership${suffix}Pct`, this.calcPct(v(`leadership${suffix}`), v('leadershipTotal')));
      // The Employees sub-table is measured against whichever base Total No's type selects,
      // the same denominator the live recalculation uses.
      setPct(`employees${suffix}Pct`, this.calcPct(v(`employees${suffix}`), base));
      setPct(`board${suffix}Pct`, this.calcPct(v(`board${suffix}`), this.boardMemberCount));
    }
  }

  private patchForm(data: CompanyEmployeeMetricsDto): void {
    this.form.patchValue({
      total: data.total,
      totalPct: data.totalPct,
      totalType: data.totalType ?? 0,
      permanent: data.permanent,
      permanentPct: data.permanentPct,
      temporary: data.temporary,
      temporaryPct: data.temporaryPct,
      fullTime: data.fullTime,
      fullTimePct: data.fullTimePct,
      partTime: data.partTime,
      partTimePct: data.partTimePct,
      contractors: data.contractors,
      newEmployeeHires: data.newEmployeeHires,
      newEmployeeHiresPct: data.newEmployeeHiresPct,
      employeeTurnover: data.employeeTurnover,
      employeeTurnoverPct: data.employeeTurnoverPct,
      voluntaryTurnover: data.voluntaryTurnover,
      voluntaryTurnoverPct: data.voluntaryTurnoverPct,
      injuryRate: data.injuryRate,
      accidents: data.accidents,
      fatalities: data.fatalities,
      lostTimeIncidentRate: data.lostTimeIncidentRate,
      totalRecordableIncidentRate: data.totalRecordableIncidentRate,
      lostWorkingDays: data.lostWorkingDays,
      absenteeRate: data.absenteeRate,
      contractorsInjuryRate: data.contractorsInjuryRate,
      contractorsAccidents: data.contractorsAccidents,
      contractorsFatalities: data.contractorsFatalities,
      contractorsLostTimeIncidentRate: data.contractorsLostTimeIncidentRate,
      contractorsTotalRecordableIncidentRate: data.contractorsTotalRecordableIncidentRate,
      contractorsLostWorkingDays: data.contractorsLostWorkingDays,
      contractorsAbsenteeRate: data.contractorsAbsenteeRate,
      totalAccidents: data.totalAccidents,
      totalFatalities: data.totalFatalities,
      totalLostWorkingDays: data.totalLostWorkingDays,
      newHiresMale: data.newHiresMale,
      newHiresMalePct: data.newHiresMalePct,
      newHiresFemale: data.newHiresFemale,
      newHiresFemalePct: data.newHiresFemalePct,
      newHiresLgbt: data.newHiresLgbt,
      newHiresLgbtPct: data.newHiresLgbtPct,
      newHiresUnderThirty: data.newHiresUnderThirty,
      newHiresUnderThirtyPct: data.newHiresUnderThirtyPct,
      newHiresOverThirtyUnderFifty: data.newHiresOverThirtyUnderFifty,
      newHiresOverThirtyUnderFiftyPct: data.newHiresOverThirtyUnderFiftyPct,
      newHiresOverFifty: data.newHiresOverFifty,
      newHiresOverFiftyPct: data.newHiresOverFiftyPct,
      newHiresNonClassified: data.newHiresNonClassified,
      newHiresNonClassifiedPct: data.newHiresNonClassifiedPct,
      newHiresAmerica: data.newHiresAmerica,
      newHiresAmericaPct: data.newHiresAmericaPct,
      newHiresAsiaPacific: data.newHiresAsiaPacific,
      newHiresAsiaPacificPct: data.newHiresAsiaPacificPct,
      newHiresEuropeAfrica: data.newHiresEuropeAfrica,
      newHiresEuropeAfricaPct: data.newHiresEuropeAfricaPct,
      newHiresOtherRegion: data.newHiresOtherRegion,
      newHiresOtherRegionPct: data.newHiresOtherRegionPct,
      newHiresWhites: data.newHiresWhites,
      newHiresWhitesPct: data.newHiresWhitesPct,
      newHiresBlacks: data.newHiresBlacks,
      newHiresBlacksPct: data.newHiresBlacksPct,
      newHiresAsian: data.newHiresAsian,
      newHiresAsianPct: data.newHiresAsianPct,
      newHiresHispanic: data.newHiresHispanic,
      newHiresHispanicPct: data.newHiresHispanicPct,
      newHiresOtherMinority: data.newHiresOtherMinority,
      newHiresOtherMinorityPct: data.newHiresOtherMinorityPct,
      turnoverMale: data.turnoverMale,
      turnoverMalePct: data.turnoverMalePct,
      turnoverFemale: data.turnoverFemale,
      turnoverFemalePct: data.turnoverFemalePct,
      turnoverLgbt: data.turnoverLgbt,
      turnoverLgbtPct: data.turnoverLgbtPct,
      turnoverUnderThirty: data.turnoverUnderThirty,
      turnoverUnderThirtyPct: data.turnoverUnderThirtyPct,
      turnoverOverThirtyUnderFifty: data.turnoverOverThirtyUnderFifty,
      turnoverOverThirtyUnderFiftyPct: data.turnoverOverThirtyUnderFiftyPct,
      turnoverOverFifty: data.turnoverOverFifty,
      turnoverOverFiftyPct: data.turnoverOverFiftyPct,
      turnoverNonClassified: data.turnoverNonClassified,
      turnoverNonClassifiedPct: data.turnoverNonClassifiedPct,
      turnoverAmerica: data.turnoverAmerica,
      turnoverAmericaPct: data.turnoverAmericaPct,
      turnoverAsiaPacific: data.turnoverAsiaPacific,
      turnoverAsiaPacificPct: data.turnoverAsiaPacificPct,
      turnoverEuropeAfrica: data.turnoverEuropeAfrica,
      turnoverEuropeAfricaPct: data.turnoverEuropeAfricaPct,
      turnoverOtherRegion: data.turnoverOtherRegion,
      turnoverOtherRegionPct: data.turnoverOtherRegionPct,
      turnoverWhites: data.turnoverWhites,
      turnoverWhitesPct: data.turnoverWhitesPct,
      turnoverBlacks: data.turnoverBlacks,
      turnoverBlacksPct: data.turnoverBlacksPct,
      turnoverAsian: data.turnoverAsian,
      turnoverAsianPct: data.turnoverAsianPct,
      turnoverHispanic: data.turnoverHispanic,
      turnoverHispanicPct: data.turnoverHispanicPct,
      turnoverOtherMinority: data.turnoverOtherMinority,
      turnoverOtherMinorityPct: data.turnoverOtherMinorityPct,
      boardMale: data.boardMale,
      boardMalePct: data.boardMalePct,
      boardFemale: data.boardFemale,
      boardFemalePct: data.boardFemalePct,
      boardLgbt: data.boardLgbt,
      boardLgbtPct: data.boardLgbtPct,
      boardUnderThirty: data.boardUnderThirty,
      boardUnderThirtyPct: data.boardUnderThirtyPct,
      boardOverThirtyUnderFifty: data.boardOverThirtyUnderFifty,
      boardOverThirtyUnderFiftyPct: data.boardOverThirtyUnderFiftyPct,
      boardOverFifty: data.boardOverFifty,
      boardOverFiftyPct: data.boardOverFiftyPct,
      boardNonClassified: data.boardNonClassified,
      boardNonClassifiedPct: data.boardNonClassifiedPct,
      boardAmerica: data.boardAmerica,
      boardAmericaPct: data.boardAmericaPct,
      boardAsiaPacific: data.boardAsiaPacific,
      boardAsiaPacificPct: data.boardAsiaPacificPct,
      boardEuropeAfrica: data.boardEuropeAfrica,
      boardEuropeAfricaPct: data.boardEuropeAfricaPct,
      boardOtherRegion: data.boardOtherRegion,
      boardOtherRegionPct: data.boardOtherRegionPct,
      boardWhites: data.boardWhites,
      boardWhitesPct: data.boardWhitesPct,
      boardBlacks: data.boardBlacks,
      boardBlacksPct: data.boardBlacksPct,
      boardAsian: data.boardAsian,
      boardAsianPct: data.boardAsianPct,
      boardHispanic: data.boardHispanic,
      boardHispanicPct: data.boardHispanicPct,
      boardOtherMinority: data.boardOtherMinority,
      boardOtherMinorityPct: data.boardOtherMinorityPct,
      leadershipMale: data.leadershipMale,
      leadershipMalePct: data.leadershipMalePct,
      leadershipFemale: data.leadershipFemale,
      leadershipFemalePct: data.leadershipFemalePct,
      leadershipLgbt: data.leadershipLgbt,
      leadershipLgbtPct: data.leadershipLgbtPct,
      leadershipUnderThirty: data.leadershipUnderThirty,
      leadershipUnderThirtyPct: data.leadershipUnderThirtyPct,
      leadershipOverThirtyUnderFifty: data.leadershipOverThirtyUnderFifty,
      leadershipOverThirtyUnderFiftyPct: data.leadershipOverThirtyUnderFiftyPct,
      leadershipOverFifty: data.leadershipOverFifty,
      leadershipOverFiftyPct: data.leadershipOverFiftyPct,
      leadershipNonClassified: data.leadershipNonClassified,
      leadershipNonClassifiedPct: data.leadershipNonClassifiedPct,
      leadershipAmerica: data.leadershipAmerica,
      leadershipAmericaPct: data.leadershipAmericaPct,
      leadershipAsiaPacific: data.leadershipAsiaPacific,
      leadershipAsiaPacificPct: data.leadershipAsiaPacificPct,
      leadershipEuropeAfrica: data.leadershipEuropeAfrica,
      leadershipEuropeAfricaPct: data.leadershipEuropeAfricaPct,
      leadershipOtherRegion: data.leadershipOtherRegion,
      leadershipOtherRegionPct: data.leadershipOtherRegionPct,
      leadershipWhites: data.leadershipWhites,
      leadershipWhitesPct: data.leadershipWhitesPct,
      leadershipBlacks: data.leadershipBlacks,
      leadershipBlacksPct: data.leadershipBlacksPct,
      leadershipAsian: data.leadershipAsian,
      leadershipAsianPct: data.leadershipAsianPct,
      leadershipHispanic: data.leadershipHispanic,
      leadershipHispanicPct: data.leadershipHispanicPct,
      leadershipOtherMinority: data.leadershipOtherMinority,
      leadershipOtherMinorityPct: data.leadershipOtherMinorityPct,
      employeesMale: data.employeesMale,
      employeesMalePct: data.employeesMalePct,
      employeesFemale: data.employeesFemale,
      employeesFemalePct: data.employeesFemalePct,
      employeesLgbt: data.employeesLgbt,
      employeesLgbtPct: data.employeesLgbtPct,
      employeesUnderThirty: data.employeesUnderThirty,
      employeesUnderThirtyPct: data.employeesUnderThirtyPct,
      employeesOverThirtyUnderFifty: data.employeesOverThirtyUnderFifty,
      employeesOverThirtyUnderFiftyPct: data.employeesOverThirtyUnderFiftyPct,
      employeesOverFifty: data.employeesOverFifty,
      employeesOverFiftyPct: data.employeesOverFiftyPct,
      employeesNonClassified: data.employeesNonClassified,
      employeesNonClassifiedPct: data.employeesNonClassifiedPct,
      employeesAmerica: data.employeesAmerica,
      employeesAmericaPct: data.employeesAmericaPct,
      employeesAsiaPacific: data.employeesAsiaPacific,
      employeesAsiaPacificPct: data.employeesAsiaPacificPct,
      employeesEuropeAfrica: data.employeesEuropeAfrica,
      employeesEuropeAfricaPct: data.employeesEuropeAfricaPct,
      employeesOtherRegion: data.employeesOtherRegion,
      employeesOtherRegionPct: data.employeesOtherRegionPct,
      employeesWhites: data.employeesWhites,
      employeesWhitesPct: data.employeesWhitesPct,
      employeesBlacks: data.employeesBlacks,
      employeesBlacksPct: data.employeesBlacksPct,
      employeesAsian: data.employeesAsian,
      employeesAsianPct: data.employeesAsianPct,
      employeesHispanic: data.employeesHispanic,
      employeesHispanicPct: data.employeesHispanicPct,
      employeesOtherMinority: data.employeesOtherMinority,
      employeesOtherMinorityPct: data.employeesOtherMinorityPct,
      averageAge: data.averageAge,
      leadershipTotal: data.leadershipTotal,
      disabledEmployees: data.disabledEmployees,
      disabledEmployeesPct: data.disabledEmployeesPct,
      genderPayGap: data.genderPayGap,
      averageMaleSalary: data.averageMaleSalary,
      averageMaleSalaryCurrencyId: data.averageMaleSalaryCurrencyId ?? '',
      averageFemaleSalary: data.averageFemaleSalary,
      averageFemaleSalaryCurrencyId: data.averageFemaleSalaryCurrencyId ?? '',
      entitledToParentalLeave: data.entitledToParentalLeave,
      tookToParentalLeave: data.tookToParentalLeave,
      returnedFromParentalLeave: data.returnedFromParentalLeave,
      trainingCosts: data.trainingCosts,
      trainingCostsCurrencyId: data.trainingCostsCurrencyId ?? '',
      workingHours: data.workingHours,
      employeeHealthTrainingHours: data.employeeHealthTrainingHours,
      trainingEmployees: data.trainingEmployees,
      trainingHoursPerEmployee: data.trainingHoursPerEmployee,
      tradeUnionRepresentationRate: data.tradeUnionRepresentationRate,
      collectiveAgreementRate: data.collectiveAgreementRate,
      comments: data.comments ?? ''
    }, { emitEvent: false });

    // Which side of each number/percentage pair drives the other. A row that was saved as a
    // percentage alone counts as percentage-driven, so filling in its total later works out the
    // number instead of wiping the percentage.
    this.permTempLastEdited =
      data.permanent != null ? 'permanent'
        : data.temporary != null ? 'temporary'
          : data.permanentPct != null ? 'permanentPct'
            : data.temporaryPct != null ? 'temporaryPct' : null;
    this.fullPartLastEdited =
      data.fullTime != null ? 'fullTime'
        : data.partTime != null ? 'partTime'
          : data.fullTimePct != null ? 'fullTimePct'
            : data.partTimePct != null ? 'partTimePct' : null;
    this.hiresLastEdited = data.newEmployeeHires != null ? 'newEmployeeHires'
      : (data.newEmployeeHiresPct != null ? 'newEmployeeHiresPct' : null);
    this.turnoverLastEdited = data.employeeTurnover != null ? 'employeeTurnover'
      : (data.employeeTurnoverPct != null ? 'employeeTurnoverPct' : null);
    this.voluntaryLastEdited = data.voluntaryTurnover != null ? 'voluntaryTurnover'
      : (data.voluntaryTurnoverPct != null ? 'voluntaryTurnoverPct' : null);
    this.disabledLastEdited = data.disabledEmployees != null ? 'num'
      : (data.disabledEmployeesPct != null ? 'pct' : null);
    this.hsTotalManual = {
      accidents: data.totalAccidents != null && data.totalAccidents !== this.safeSum(data.accidents, data.contractorsAccidents),
      fatalities: data.totalFatalities != null && data.totalFatalities !== this.safeSum(data.fatalities, data.contractorsFatalities),
      lostWorkingDays: data.totalLostWorkingDays != null && data.totalLostWorkingDays !== this.safeSum(data.lostWorkingDays, data.contractorsLostWorkingDays)
    };
    this.newHiresDemoLastEdited = {};
    this.turnoverDemoLastEdited = {};
    this.leadershipDemoLastEdited = {};
    this.employeesDemoLastEdited = {};
    this.genderLastEdited = {};
    const drivenBy = (prefix: string, suffix: string): 'num' | 'pct' | null => {
      if (data[`${prefix}${suffix}` as keyof CompanyEmployeeMetricsDto] != null) return 'num';
      return data[`${prefix}${suffix}Pct` as keyof CompanyEmployeeMetricsDto] != null ? 'pct' : null;
    };
    for (const suffix of this.demoSuffixes) {
      this.newHiresDemoLastEdited[suffix] = drivenBy('newHires', suffix);
      this.turnoverDemoLastEdited[suffix] = drivenBy('turnover', suffix);
      this.leadershipDemoLastEdited[suffix] = drivenBy('leadership', suffix);
      this.employeesDemoLastEdited[suffix] = drivenBy('employees', suffix);
    }
    this.recalculateAll();
    this.form.markAsPristine();
  }

  /**
   * @param currencyId The currency every amount defaults to — the company's country currency,
   * or '' when there is none to fall back to.
   */
  private resetForm(currencyId: string = ''): void {
    this.permTempLastEdited = null;
    this.fullPartLastEdited = null;
    this.hiresLastEdited = null;
    this.turnoverLastEdited = null;
    this.voluntaryLastEdited = null;
    this.disabledLastEdited = null;
    this.hsTotalManual = { accidents: false, fatalities: false, lostWorkingDays: false };
    this.newHiresDemoLastEdited = {};
    this.turnoverDemoLastEdited = {};
    this.leadershipDemoLastEdited = {};
    this.employeesDemoLastEdited = {};
    this.genderLastEdited = {};
    this.boardMemberCount = null;
    const keywordsValue = this.form.get('keywords')!.value;
    this.form.reset({
      total: null,
      totalPct: null,
      totalType: 0,
      permanent: null,
      permanentPct: null,
      temporary: null,
      temporaryPct: null,
      fullTime: null,
      fullTimePct: null,
      partTime: null,
      partTimePct: null,
      contractors: null,
      newEmployeeHires: null,
      newEmployeeHiresPct: null,
      employeeTurnover: null,
      employeeTurnoverPct: null,
      voluntaryTurnover: null,
      voluntaryTurnoverPct: null,
      injuryRate: null,
      accidents: null,
      fatalities: null,
      lostTimeIncidentRate: null,
      totalRecordableIncidentRate: null,
      lostWorkingDays: null,
      absenteeRate: null,
      contractorsInjuryRate: null,
      contractorsAccidents: null,
      contractorsFatalities: null,
      contractorsLostTimeIncidentRate: null,
      contractorsTotalRecordableIncidentRate: null,
      contractorsLostWorkingDays: null,
      contractorsAbsenteeRate: null,
      totalAccidents: null,
      totalFatalities: null,
      totalLostWorkingDays: null,
      newHiresMale: null, newHiresMalePct: null,
      newHiresFemale: null, newHiresFemalePct: null,
      newHiresLgbt: null,
      newHiresLgbtPct: null,
      newHiresUnderThirty: null, newHiresUnderThirtyPct: null,
      newHiresOverThirtyUnderFifty: null, newHiresOverThirtyUnderFiftyPct: null,
      newHiresOverFifty: null, newHiresOverFiftyPct: null,
      newHiresNonClassified: null, newHiresNonClassifiedPct: null,
      newHiresAmerica: null, newHiresAmericaPct: null,
      newHiresAsiaPacific: null, newHiresAsiaPacificPct: null,
      newHiresEuropeAfrica: null, newHiresEuropeAfricaPct: null,
      newHiresOtherRegion: null, newHiresOtherRegionPct: null,
      newHiresWhites: null, newHiresWhitesPct: null,
      newHiresBlacks: null, newHiresBlacksPct: null,
      newHiresAsian: null, newHiresAsianPct: null,
      newHiresHispanic: null, newHiresHispanicPct: null,
      newHiresOtherMinority: null, newHiresOtherMinorityPct: null,
      turnoverMale: null, turnoverMalePct: null,
      turnoverFemale: null, turnoverFemalePct: null,
      turnoverLgbt: null,
      turnoverLgbtPct: null,
      turnoverUnderThirty: null, turnoverUnderThirtyPct: null,
      turnoverOverThirtyUnderFifty: null, turnoverOverThirtyUnderFiftyPct: null,
      turnoverOverFifty: null, turnoverOverFiftyPct: null,
      turnoverNonClassified: null, turnoverNonClassifiedPct: null,
      turnoverAmerica: null, turnoverAmericaPct: null,
      turnoverAsiaPacific: null, turnoverAsiaPacificPct: null,
      turnoverEuropeAfrica: null, turnoverEuropeAfricaPct: null,
      turnoverOtherRegion: null, turnoverOtherRegionPct: null,
      turnoverWhites: null, turnoverWhitesPct: null,
      turnoverBlacks: null, turnoverBlacksPct: null,
      turnoverAsian: null, turnoverAsianPct: null,
      turnoverHispanic: null, turnoverHispanicPct: null,
      turnoverOtherMinority: null, turnoverOtherMinorityPct: null,
      boardMale: null, boardMalePct: null,
      boardFemale: null, boardFemalePct: null,
      boardLgbt: null,
      boardLgbtPct: null,
      boardUnderThirty: null, boardUnderThirtyPct: null,
      boardOverThirtyUnderFifty: null, boardOverThirtyUnderFiftyPct: null,
      boardOverFifty: null, boardOverFiftyPct: null,
      boardNonClassified: null, boardNonClassifiedPct: null,
      boardAmerica: null, boardAmericaPct: null,
      boardAsiaPacific: null, boardAsiaPacificPct: null,
      boardEuropeAfrica: null, boardEuropeAfricaPct: null,
      boardOtherRegion: null, boardOtherRegionPct: null,
      boardWhites: null, boardWhitesPct: null,
      boardBlacks: null, boardBlacksPct: null,
      boardAsian: null, boardAsianPct: null,
      boardHispanic: null, boardHispanicPct: null,
      boardOtherMinority: null, boardOtherMinorityPct: null,
      leadershipMale: null, leadershipMalePct: null,
      leadershipFemale: null, leadershipFemalePct: null,
      leadershipLgbt: null,
      leadershipLgbtPct: null,
      leadershipUnderThirty: null, leadershipUnderThirtyPct: null,
      leadershipOverThirtyUnderFifty: null, leadershipOverThirtyUnderFiftyPct: null,
      leadershipOverFifty: null, leadershipOverFiftyPct: null,
      leadershipNonClassified: null, leadershipNonClassifiedPct: null,
      leadershipAmerica: null, leadershipAmericaPct: null,
      leadershipAsiaPacific: null, leadershipAsiaPacificPct: null,
      leadershipEuropeAfrica: null, leadershipEuropeAfricaPct: null,
      leadershipOtherRegion: null, leadershipOtherRegionPct: null,
      leadershipWhites: null, leadershipWhitesPct: null,
      leadershipBlacks: null, leadershipBlacksPct: null,
      leadershipAsian: null, leadershipAsianPct: null,
      leadershipHispanic: null, leadershipHispanicPct: null,
      leadershipOtherMinority: null, leadershipOtherMinorityPct: null,
      employeesMale: null, employeesMalePct: null,
      employeesFemale: null, employeesFemalePct: null,
      employeesLgbt: null,
      employeesLgbtPct: null,
      employeesUnderThirty: null, employeesUnderThirtyPct: null,
      employeesOverThirtyUnderFifty: null, employeesOverThirtyUnderFiftyPct: null,
      employeesOverFifty: null, employeesOverFiftyPct: null,
      employeesNonClassified: null, employeesNonClassifiedPct: null,
      employeesAmerica: null, employeesAmericaPct: null,
      employeesAsiaPacific: null, employeesAsiaPacificPct: null,
      employeesEuropeAfrica: null, employeesEuropeAfricaPct: null,
      employeesOtherRegion: null, employeesOtherRegionPct: null,
      employeesWhites: null, employeesWhitesPct: null,
      employeesBlacks: null, employeesBlacksPct: null,
      employeesAsian: null, employeesAsianPct: null,
      employeesHispanic: null, employeesHispanicPct: null,
      employeesOtherMinority: null, employeesOtherMinorityPct: null,
      averageAge: null,
      leadershipTotal: null,
      disabledEmployees: null,
      disabledEmployeesPct: null,
      genderPayGap: null,
      averageMaleSalary: null,
      averageMaleSalaryCurrencyId: currencyId,
      averageFemaleSalary: null,
      averageFemaleSalaryCurrencyId: currencyId,
      entitledToParentalLeave: null,
      tookToParentalLeave: null,
      returnedFromParentalLeave: null,
      trainingCosts: null,
      trainingCostsCurrencyId: currencyId,
      workingHours: null,
      employeeHealthTrainingHours: null,
      trainingEmployees: null,
      trainingHoursPerEmployee: null,
      tradeUnionRepresentationRate: null,
      collectiveAgreementRate: null,
      comments: ''
    }, { emitEvent: false });

    this.form.get('keywords')!.patchValue(keywordsValue, { emitEvent: false });
    this.recalculateAll();
    this.form.markAsPristine();
  }
}
